import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  HelpCircle,
  Search,
  Users,
  Brain,
  Target,
  Mail,
  Kanban,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Info,
  Lightbulb
} from 'lucide-react';

const Help = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'Overview', icon: HelpCircle },
    { id: 'categories-segments', label: 'Categories vs Segments', icon: Users },
    { id: 'ai-scoring', label: 'AI Scoring Logic', icon: Brain },
    { id: 'campaigns', label: 'Running Campaigns', icon: Target },
    { id: 'leads', label: 'Managing Leads', icon: Kanban },
    { id: 'outreach', label: 'Email Outreach', icon: Mail },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="help-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Help & Documentation</h1>
          <p className="text-muted-foreground mt-1">Learn how Rise works and get the most out of your lead generation</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 flex-shrink-0">
          <Card className="bg-white border-stone-100 sticky top-6">
            <CardContent className="p-4">
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeSection === section.id
                          ? 'bg-forest/10 text-forest'
                          : 'text-muted-foreground hover:bg-sage/50 hover:text-foreground'
                      }`}
                      data-testid={`nav-${section.id}`}
                    >
                      <Icon className="h-4 w-4" />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-6">
          {/* Overview Section */}
          {activeSection === 'overview' && (
            <div className="space-y-6">
              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-forest" />
                    What is Rise?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Rise is an AI-powered lead generation and outreach platform designed specifically for wholesale bakeries. 
                    It automates the process of finding potential B2B clients, scoring them based on fit, and helping you 
                    craft personalized outreach.
                  </p>
                  
                  <div className="grid sm:grid-cols-3 gap-4 pt-4">
                    <div className="p-4 bg-sage/30 rounded-lg">
                      <Search className="h-8 w-8 text-forest mb-2" />
                      <h3 className="font-medium mb-1">Discover</h3>
                      <p className="text-sm text-muted-foreground">Find potential clients across multiple online sources</p>
                    </div>
                    <div className="p-4 bg-sage/30 rounded-lg">
                      <Sparkles className="h-8 w-8 text-wheat mb-2" />
                      <h3 className="font-medium mb-1">Score</h3>
                      <p className="text-sm text-muted-foreground">AI analyzes and ranks leads by potential value</p>
                    </div>
                    <div className="p-4 bg-sage/30 rounded-lg">
                      <Mail className="h-8 w-8 text-forest mb-2" />
                      <h3 className="font-medium mb-1">Outreach</h3>
                      <p className="text-sm text-muted-foreground">Draft personalized emails with AI assistance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">How It Works</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-forest text-white rounded-full flex items-center justify-center font-bold">1</div>
                      <div>
                        <h4 className="font-medium">Create a Campaign</h4>
                        <p className="text-sm text-muted-foreground">Define what types of businesses you're looking for and where</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-forest text-white rounded-full flex items-center justify-center font-bold">2</div>
                      <div>
                        <h4 className="font-medium">Run Discovery</h4>
                        <p className="text-sm text-muted-foreground">Rise searches Google Maps, Yelp, news sources, and more</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-forest text-white rounded-full flex items-center justify-center font-bold">3</div>
                      <div>
                        <h4 className="font-medium">AI Analysis</h4>
                        <p className="text-sm text-muted-foreground">Claude AI scores each lead and recommends the best segment fit</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-forest text-white rounded-full flex items-center justify-center font-bold">4</div>
                      <div>
                        <h4 className="font-medium">Review & Outreach</h4>
                        <p className="text-sm text-muted-foreground">Review leads in your pipeline and send personalized emails</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Categories vs Segments Section */}
          {activeSection === 'categories-segments' && (
            <div className="space-y-6">
              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Users className="h-5 w-5 text-forest" />
                    Understanding Categories vs Segments
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">
                        <strong>Key Insight:</strong> Categories are <em>search terms</em> used to find businesses. 
                        Segments are <em>customer profiles</em> that describe how to approach different types of clients.
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Categories */}
                    <div className="p-5 bg-sage/30 rounded-lg border border-sage">
                      <div className="flex items-center gap-2 mb-3">
                        <Search className="h-5 w-5 text-forest" />
                        <h3 className="font-heading font-semibold text-lg">Categories</h3>
                        <Badge variant="outline" className="ml-auto">Search Terms</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Categories are the keywords used when searching for businesses during discovery.
                      </p>
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Examples:</p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary">coffee shop</Badge>
                          <Badge variant="secondary">restaurant</Badge>
                          <Badge variant="secondary">hotel</Badge>
                          <Badge variant="secondary">catering</Badge>
                          <Badge variant="secondary">cafe</Badge>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-sage">
                        <p className="text-sm text-muted-foreground">
                          <strong>Where defined:</strong> In your Campaign settings under "Search Categories"
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          <strong>Stored on lead:</strong> The <code className="bg-stone-100 px-1 rounded">category</code> field shows what type of business was found
                        </p>
                      </div>
                    </div>

                    {/* Segments */}
                    <div className="p-5 bg-forest/5 rounded-lg border border-forest/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="h-5 w-5 text-forest" />
                        <h3 className="font-heading font-semibold text-lg">Segments</h3>
                        <Badge variant="outline" className="ml-auto bg-forest/10">Customer Profiles</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Segments are detailed customer personas that help Claude understand how to approach each type of client.
                      </p>
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Examples:</p>
                        <div className="flex flex-wrap gap-2">
                          <Badge className="bg-forest/20 text-forest hover:bg-forest/30">Coffee Shops</Badge>
                          <Badge className="bg-forest/20 text-forest hover:bg-forest/30">Hotels</Badge>
                          <Badge className="bg-forest/20 text-forest hover:bg-forest/30">Corporate Catering</Badge>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-forest/20">
                        <p className="text-sm text-muted-foreground">
                          <strong>Where defined:</strong> In the Segments page with detailed profile descriptions
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          <strong>Assigned by:</strong> Claude AI recommends the best-fit segment during analysis
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Flow Diagram */}
                  <div className="p-5 bg-stone-50 rounded-lg">
                    <h4 className="font-medium mb-4">How They Work Together</h4>
                    <div className="flex flex-col sm:flex-row items-center gap-4 text-center">
                      <div className="p-3 bg-white rounded-lg border shadow-sm flex-1 w-full">
                        <p className="text-xs text-muted-foreground mb-1">Step 1</p>
                        <p className="font-medium text-sm">Campaign searches for</p>
                        <Badge variant="secondary" className="mt-1">coffee shop</Badge>
                        <p className="text-xs text-muted-foreground mt-1">(category)</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0 rotate-90 sm:rotate-0" />
                      <div className="p-3 bg-white rounded-lg border shadow-sm flex-1 w-full">
                        <p className="text-xs text-muted-foreground mb-1">Step 2</p>
                        <p className="font-medium text-sm">Rise finds</p>
                        <p className="text-forest font-medium mt-1">"Blue Bottle Coffee"</p>
                        <p className="text-xs text-muted-foreground mt-1">(lead)</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0 rotate-90 sm:rotate-0" />
                      <div className="p-3 bg-white rounded-lg border shadow-sm flex-1 w-full">
                        <p className="text-xs text-muted-foreground mb-1">Step 3</p>
                        <p className="font-medium text-sm">Claude assigns</p>
                        <Badge className="bg-forest/20 text-forest hover:bg-forest/30 mt-1">Coffee Shops</Badge>
                        <p className="text-xs text-muted-foreground mt-1">(segment)</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Segment Profiles in Detail</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Each segment has a detailed profile that tells Claude what matters to that type of customer. 
                    This helps with scoring and crafting personalized outreach.
                  </p>
                  
                  <div className="p-4 bg-sage/30 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-wheat" />
                      Example: Coffee Shops Segment Profile
                    </h4>
                    <p className="text-sm text-muted-foreground italic">
                      "Coffee shops care about consistent quality baked goods that complement their beverage menu, 
                      reliable daily delivery schedules, and products that fit their brand aesthetic and price point. 
                      They value artisan presentation and want items that pair well with specialty coffee. 
                      Morning rush is critical - they need dependable early delivery."
                    </p>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    This profile helps Claude understand that when emailing a coffee shop, it should emphasize 
                    early delivery reliability, artisan quality, and coffee pairings.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* AI Scoring Logic Section */}
          {activeSection === 'ai-scoring' && (
            <div className="space-y-6">
              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Brain className="h-5 w-5 text-forest" />
                    How Claude AI Scores Leads
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-muted-foreground">
                    Rise uses Claude AI (Anthropic's advanced language model) to analyze each discovered lead 
                    and provide intelligent scoring and recommendations.
                  </p>

                  <div className="space-y-4">
                    <h4 className="font-medium">What Claude Receives</h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-stone-50 rounded-lg">
                        <p className="font-medium text-sm mb-2">Lead Information</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• Business name</li>
                          <li>• Business type/category</li>
                          <li>• Location (city, state)</li>
                          <li>• Rating (if available)</li>
                          <li>• Website (if available)</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-stone-50 rounded-lg">
                        <p className="font-medium text-sm mb-2">Your Segment Profiles</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• All segment names</li>
                          <li>• Detailed profile descriptions</li>
                          <li>• What each segment cares about</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium">What Claude Returns</h4>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-4 bg-sage/30 rounded-lg text-center">
                        <div className="text-2xl font-bold text-forest mb-1">85</div>
                        <p className="text-sm font-medium">Score</p>
                        <p className="text-xs text-muted-foreground">0-100 potential fit</p>
                      </div>
                      <div className="p-4 bg-sage/30 rounded-lg text-center">
                        <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-1" />
                        <p className="text-sm font-medium">Relevant</p>
                        <p className="text-xs text-muted-foreground">Yes/No filter</p>
                      </div>
                      <div className="p-4 bg-sage/30 rounded-lg text-center">
                        <Badge className="bg-forest/20 text-forest">Coffee Shops</Badge>
                        <p className="text-sm font-medium mt-2">Segment</p>
                        <p className="text-xs text-muted-foreground">Best fit match</p>
                      </div>
                      <div className="p-4 bg-sage/30 rounded-lg text-center">
                        <Sparkles className="h-6 w-6 text-wheat mx-auto mb-1" />
                        <p className="text-sm font-medium">Outreach Angle</p>
                        <p className="text-xs text-muted-foreground">Suggested approach</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Info className="h-5 w-5 text-amber-600" />
                      Relevance Filtering
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Claude is instructed to reject businesses that can't realistically buy wholesale baked goods. 
                      Law firms, doctors, accountants, and non-food retail stores are automatically filtered out 
                      with a <code className="bg-amber-100 px-1 rounded">relevant: false</code> flag.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium">Score Ranges</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-8 bg-green-100 border border-green-300 rounded flex items-center justify-center text-sm font-medium text-green-700">70-100</div>
                        <p className="text-sm"><strong>High potential</strong> - Strong fit, prioritize outreach</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-8 bg-yellow-100 border border-yellow-300 rounded flex items-center justify-center text-sm font-medium text-yellow-700">40-69</div>
                        <p className="text-sm"><strong>Medium potential</strong> - Worth reviewing, may need more research</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-8 bg-red-100 border border-red-300 rounded flex items-center justify-center text-sm font-medium text-red-700">0-39</div>
                        <p className="text-sm"><strong>Low potential</strong> - Limited fit, consider archiving</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Rescoring Leads</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    You can rescore leads at any time to get updated analysis. This is useful when:
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                    <li>• You've updated your segment profiles</li>
                    <li>• Time has passed and you want fresh analysis</li>
                    <li>• You want to check if a business is still active</li>
                  </ul>
                  <p className="text-sm text-muted-foreground">
                    During rescoring, Rise also checks the business website for signs that the business 
                    may have closed or moved, which can affect the score.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Running Campaigns Section */}
          {activeSection === 'campaigns' && (
            <div className="space-y-6">
              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Target className="h-5 w-5 text-forest" />
                    Running Discovery Campaigns
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Coming Soon:</strong> Detailed step-by-step instructions for creating and running campaigns.
                    </p>
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="create">
                      <AccordionTrigger>Creating a New Campaign</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p><strong>1. Campaign Name:</strong> Give your campaign a descriptive name (e.g., "Q1 Boston Coffee Shops")</p>
                          <p><strong>2. Target Markets:</strong> Enter the cities or regions you want to search (e.g., "Boston, MA", "Cambridge, MA")</p>
                          <p><strong>3. Search Categories:</strong> Define the types of businesses to find (e.g., "coffee shop", "bakery cafe", "breakfast restaurant")</p>
                          <p><strong>4. Keywords:</strong> Optional additional search terms to refine results</p>
                          <p className="italic mt-2">More detailed instructions coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="run">
                      <AccordionTrigger>Running Discovery</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p><strong>1.</strong> Click "Run Discovery" on your campaign card</p>
                          <p><strong>2.</strong> Rise will search multiple sources: Google Maps, Yelp, Bing, news sites, and YouTube</p>
                          <p><strong>3.</strong> Each lead is analyzed by Claude AI for scoring and segment assignment</p>
                          <p><strong>4.</strong> Progress is shown in real-time - this can take several minutes for large searches</p>
                          <p><strong>5.</strong> When complete, new leads appear in your Pipeline</p>
                          <p className="italic mt-2">More detailed instructions coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="sources">
                      <AccordionTrigger>Discovery Sources</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <p><strong>Google Maps:</strong> Local business listings with ratings and contact info</p>
                          <p><strong>Yelp:</strong> Restaurant and cafe listings with reviews</p>
                          <p><strong>Bing Maps:</strong> Additional local business data</p>
                          <p><strong>Google News:</strong> Recent news about businesses in your target area</p>
                          <p><strong>Bing News:</strong> Additional news coverage</p>
                          <p><strong>DuckDuckGo News:</strong> Privacy-focused news search</p>
                          <p><strong>YouTube:</strong> Food influencer videos mentioning local businesses</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="tips">
                      <AccordionTrigger>Tips for Better Results</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p><strong>Be specific with locations:</strong> "Boston, MA" works better than just "Boston"</p>
                          <p><strong>Use varied categories:</strong> Try "coffee shop", "cafe", "espresso bar" to catch different listings</p>
                          <p><strong>Start small:</strong> Test with one market before expanding to multiple cities</p>
                          <p><strong>Review segments first:</strong> Make sure your segment profiles are detailed and accurate</p>
                          <p className="italic mt-2">More tips coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Managing Leads Section */}
          {activeSection === 'leads' && (
            <div className="space-y-6">
              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Kanban className="h-5 w-5 text-forest" />
                    Managing Your Lead Pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Coming Soon:</strong> Detailed instructions for pipeline management and lead workflows.
                    </p>
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="stages">
                      <AccordionTrigger>Pipeline Stages</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p><strong>Discovered:</strong> New leads from discovery, awaiting review</p>
                          <p><strong>Reviewed:</strong> You've looked at the lead and confirmed interest</p>
                          <p><strong>Outreach Sent:</strong> Initial email has been sent</p>
                          <p><strong>Replied:</strong> Lead has responded to your outreach</p>
                          <p><strong>Meeting Scheduled:</strong> A meeting or call is on the calendar</p>
                          <p><strong>Active Client:</strong> Converted to a paying customer</p>
                          <p><strong>Archived:</strong> Not pursuing (closed, not interested, etc.)</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="filtering">
                      <AccordionTrigger>Filtering & Sorting</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p><strong>Search:</strong> Find leads by name, contact, or email</p>
                          <p><strong>Filter by Category:</strong> Show only specific business types</p>
                          <p><strong>Filter by City:</strong> Focus on specific locations</p>
                          <p><strong>Filter by Score:</strong> Show leads within a score range</p>
                          <p><strong>Filter by Status:</strong> Show leads in specific pipeline stages</p>
                          <p><strong>Sort:</strong> Order by score, date, or name</p>
                          <p className="italic mt-2">More filtering options coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="actions">
                      <AccordionTrigger>Lead Actions</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p><strong>View Details:</strong> Click a lead card to see full information</p>
                          <p><strong>Edit Lead:</strong> Update contact info, notes, or segment</p>
                          <p><strong>Change Stage:</strong> Drag to a new column or use the dropdown</p>
                          <p><strong>Archive:</strong> Remove from active pipeline</p>
                          <p><strong>Draft Email:</strong> Generate a personalized outreach email</p>
                          <p><strong>Schedule Meeting:</strong> Add a meeting to Google Calendar</p>
                          <p className="italic mt-2">More actions coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="manual">
                      <AccordionTrigger>Adding Leads Manually</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p>You can add leads manually from the Pipeline page:</p>
                          <p><strong>1.</strong> Click "Add Lead" button</p>
                          <p><strong>2.</strong> Enter business name and contact information</p>
                          <p><strong>3.</strong> Select a segment (optional)</p>
                          <p><strong>4.</strong> Add any notes</p>
                          <p><strong>5.</strong> Click "Create Lead"</p>
                          <p className="italic mt-2">CSV import instructions coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Email Outreach Section */}
          {activeSection === 'outreach' && (
            <div className="space-y-6">
              <Card className="bg-white border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Mail className="h-5 w-5 text-forest" />
                    Email Outreach
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Coming Soon:</strong> Complete email outreach workflow documentation.
                    </p>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-800 font-medium">Human-in-the-Loop</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Rise never sends emails automatically. Every email must be reviewed and approved by you before sending.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="drafting">
                      <AccordionTrigger>Drafting Emails with AI</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p><strong>1.</strong> Open a lead's detail page</p>
                          <p><strong>2.</strong> Click "Draft Email"</p>
                          <p><strong>3.</strong> Claude generates a personalized email based on:</p>
                          <ul className="ml-4 space-y-1">
                            <li>• The lead's business information</li>
                            <li>• Their assigned segment profile</li>
                            <li>• The suggested outreach angle</li>
                          </ul>
                          <p><strong>4.</strong> Review and edit the draft as needed</p>
                          <p className="italic mt-2">More details coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="approval">
                      <AccordionTrigger>Approving & Sending</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p><strong>1.</strong> Review the drafted email content</p>
                          <p><strong>2.</strong> Edit subject line and body as needed</p>
                          <p><strong>3.</strong> Click "Approve Email"</p>
                          <p><strong>4.</strong> Once approved, click "Send Email"</p>
                          <p><strong>5.</strong> Email is sent via your connected Gmail account</p>
                          <p className="italic mt-2">More details coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="gmail">
                      <AccordionTrigger>Gmail Integration</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p>Emails are sent through your Gmail account using Google's official API.</p>
                          <p><strong>To set up:</strong></p>
                          <p>1. Go to Settings page</p>
                          <p>2. Connect your Google account with Gmail permissions</p>
                          <p>3. Emails will be sent from your email address</p>
                          <p className="italic mt-2">More details coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="followups">
                      <AccordionTrigger>Follow-up Emails</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 text-sm text-muted-foreground">
                          <p>You can draft follow-up emails for leads who haven't responded.</p>
                          <p><strong>Best practices:</strong></p>
                          <ul className="ml-4 space-y-1">
                            <li>• Wait 5-7 days before following up</li>
                            <li>• Keep follow-ups shorter than initial outreach</li>
                            <li>• Reference your previous email</li>
                            <li>• Add new value or information</li>
                          </ul>
                          <p className="italic mt-2">Automated follow-up reminders coming soon...</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Help;
